import api from "./api";
import type { RevenueSummary, TopSoldProduct } from "../types/models";

const toNum = (v: string | number | null | undefined): number => {
  if (v == null) return 0;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

/**
 * Revenue service for API communication (Story 6-5-5).
 * Handles revenue summary for dashboard Revenue card and Order Summary.
 */
export async function getRevenueSummary(): Promise<RevenueSummary> {
  const response = await api.get<Record<string, string | number | undefined | unknown[] | object>>(
    "/revenue/summary"
  );
  const d = response.data;
  const topCustomersRaw = d.top_customers;
  const topSoldRaw = d.top_sold_products;

  const topCustomers =
    Array.isArray(topCustomersRaw) &&
    topCustomersRaw.every((c) => c && typeof c === "object" && "customer_id" in c)
      ? (topCustomersRaw as Array<{ customer_id: string; name: string; total_revenue: string | number }>).map(
          (c) => ({
            customer_id: c.customer_id,
            name: c.name,
            total_revenue: toNum(c.total_revenue),
          })
        )
      : [];

  const topSoldProducts: TopSoldProduct[] =
    Array.isArray(topSoldRaw) && topSoldRaw.every((p) => p && typeof p === "object")
      ? (topSoldRaw as Array<{ product_name?: string; quantity_sold?: number; revenue: string | number }>).map(
          (p) => ({
            product_name: p.product_name ?? "",
            quantity_sold: typeof p.quantity_sold === "number" ? p.quantity_sold : 0,
            revenue: toNum(p.revenue),
          })
        )
      : [];

  return {
    today: toNum(d.today as string | number | undefined),
    today_purchase_cost: toNum(d.today_purchase_cost as string | number | undefined),
    today_margin: toNum(d.today_margin as string | number | undefined),
    today_order_count: typeof d.today_order_count === "number" ? d.today_order_count : 0,
    last_7_days: toNum(d.last_7_days as string | number | undefined),
    last_7_days_purchase_cost: toNum(d.last_7_days_purchase_cost as string | number | undefined),
    last_7_days_margin: toNum(d.last_7_days_margin as string | number | undefined),
    last_7_days_order_count: typeof d.last_7_days_order_count === "number" ? d.last_7_days_order_count : 0,
    current_month: toNum(d.current_month as string | number | undefined),
    current_month_purchase_cost: toNum(d.current_month_purchase_cost as string | number | undefined),
    current_month_margin: toNum(d.current_month_margin as string | number | undefined),
    current_month_order_count: typeof d.current_month_order_count === "number" ? d.current_month_order_count : 0,
    last_month: toNum(d.last_month as string | number | undefined),
    last_month_purchase_cost: toNum(d.last_month_purchase_cost as string | number | undefined),
    last_month_margin: toNum(d.last_month_margin as string | number | undefined),
    last_month_order_count: typeof d.last_month_order_count === "number" ? d.last_month_order_count : 0,
    current_year_revenue: toNum(d.current_year_revenue as string | number | undefined),
    current_year_purchase_cost: toNum(d.current_year_purchase_cost as string | number | undefined),
    current_year_margin: toNum(d.current_year_margin as string | number | undefined),
    current_year_order_count: typeof d.current_year_order_count === "number" ? d.current_year_order_count : 0,
    current_week_revenue: toNum(d.current_week_revenue as string | number | undefined),
    current_week_purchase_cost: toNum(d.current_week_purchase_cost as string | number | undefined),
    current_week_margin: toNum(d.current_week_margin as string | number | undefined),
    current_week_order_count: typeof d.current_week_order_count === "number" ? d.current_week_order_count : 0,
    last_week_revenue: toNum(d.last_week_revenue as string | number | undefined),
    last_week_purchase_cost: toNum(d.last_week_purchase_cost as string | number | undefined),
    last_week_margin: toNum(d.last_week_margin as string | number | undefined),
    last_week_order_count: typeof d.last_week_order_count === "number" ? d.last_week_order_count : 0,
    total_revenue: toNum(d.total_revenue as string | number | undefined),
    collected_revenue: toNum(d.collected_revenue as string | number | undefined),
    outstanding_receivables: toNum(d.outstanding_receivables as string | number | undefined),
    top_customers: topCustomers,
    top_sold_products: topSoldProducts,
  };
}
